import { ReactNode, useMemo } from 'react';

import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import i18n from '@tecma/i18n';
import { observer } from 'mobx-react';

import { createGraphQLErrorLink } from '../helpers/createGraphQLErrorLink';
import { useStoreMobx } from '../mobx/helpers';
import { useAppSelector } from '../redux/hooks';
import { sliceEnvironment } from '../redux/slice/slice.environment';

interface Props {
  children: ReactNode;
}

export const ProviderGraphQL = observer(({ children }: Props) => {
  const store = useStoreMobx();
  const environment = useAppSelector(sliceEnvironment.selectors.environment);

  const client = useMemo(() => {
    const errorLink = createGraphQLErrorLink(
      () => store.forceLogout(true),
      () => {
        store.setSnackbar({ type: 'error', title: i18n.t('iTd.permissionDenied', 'You do not have permission to perform this operation.') });
      },
    );
    const httpLink = from([
      errorLink,
      new HttpLink({
        credentials: 'include',
        uri: environment.REACT_APP_APOLLO_URI,
      }),
    ]);

    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
        },
      };
    });

    return new ApolloClient({
      credentials: 'include',
      link: authLink.concat(httpLink),
      cache: new InMemoryCache(),
    });
  }, [environment.REACT_APP_APOLLO_URI, store]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
});
